// --- React & Redux
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import { Col } from 'antd';
import DataGrid from '@dtci/spellbook/DataGrid';
import Select from '@dtci/spellbook/Select';
// --- API & Utils
import { datastageApi } from 'datastage/api';
import { filterCriteria } from 'datastage/utils/filterCriteria';
// --- Hooks & Store
import { useAgGridApi } from 'datastage/components/AGGrid/hooks';
import { useJobMetricActions } from 'datastage/store/jobMetrics/useJobMetrics';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';
// --- Grid Utils
import { useJobMetricsGridUtils } from './utils/gridUtils';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel/InputLabel';
// --- Styles
import styles from 'datastage/components/AGGrid/details-table.module.scss';

/**
 * Job Metrics Grid
 *
 * Features:
 * - Displays a data grid of job metrics with filtering options
 * - Fetches metrics data from API based on filters and sorting
 *
 * @returns {Element}
 * @constructor
 */
export const JobMetricsGrid = () => {
    const agGridApi = useAgGridApi();
    const {
        isModalOpen,
        filterForm,
        rangeParam,
        sortParam,
        metrics,
        defaultFilterRange,
    } = useSelector((state) => state.jobMetrics);
    const { jobTypeOptions } = useSelector((s) => s.jobs);
    const showSpinner = useSelector((s) => s.spinner.showSpinner);

    const { updateJobMetrics, updateJobMetricsForm } = useJobMetricActions();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast } = useToasts();

    // Grid utilities for sorting and columns
    const { setSort, columns } = useJobMetricsGridUtils({
        datastageApi,
        filterForm,
        defaultFilterRange,
        updateJobMetrics,
        handlers: {
            setLoadingTrue,
            setLoadingFalse,
            columnHandlers: []
        }
    });

    // Fetch job metrics data from API
    const fetchJobMetrics = async () => {
        try {
            setLoadingTrue();
            const data = await datastageApi.jobMetrics(
                filterCriteria(filterForm, '', 'jobMetrics'),
                rangeParam,
                sortParam,
            );

            if (data?.code) {
                showErrorToast(data?.message || 'Failed to fetch job metrics data.');
                return;
            }
            updateJobMetrics({
                metrics: data?.jobMetricsListDTO,
                totalPages: data?.totalElements / rangeParam[1],
                totalItems: data?.totalElements,
                currentPage: 0,
            });
        } catch (error) {
            showErrorToast(error?.message || 'Failed to fetch job metrics data.');
            console.error('Error fetching job metrics:', error);
        } finally {
            setLoadingFalse();
        }
    };

    useEffect(() => {
        if (isModalOpen) {
            fetchJobMetrics();
        }
    }, [isModalOpen]);

    const onInputChange = (field, value) => {
        updateJobMetricsForm({
            key: 'filterForm',
            data: { [field]: value },
        });
    };

    return (
        <>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Job Types">
                    <Select
                        options={jobTypeOptions}
                        isClearable
                        isSearchable
                        onChange={value => onInputChange('jobType', value)}
                        value={filterForm.jobType}
                    />
                </InputLabel>
            </Col>
            <DataGrid
                className={styles.dataGrid}
                autoSizing
                agGrid={{
                    ref: agGridApi.AgGridApiRef,
                    rowData: metrics,
                    headerHeight: 45,
                    defaultColDef: {
                        wrapText: true,
                        autoHeight: true,
                        suppressMenu: true,
                        resizable: true,
                        cellStyle: { 
                            paddingTop: '10px', 
                            paddingBottom: '10px',
                            wordBreak: 'normal',
                            overflowWrap: 'break-word',
                        },
                    },
                }}
                bordered="none"
                columnDefs={columns}
                sortable={false}
                striped
                noResultsMessage={showSpinner ? 'Loading...' : 'No Job Metrics Found'}
            />
        </>
    );
};
